/**
 * La foto del precinto al abrir el silo (docs/esquema.md §5.3, v4): captura,
 * compresión y COLA DE SUBIDA a R2 por `PUT /api/fotos/llenado/{llenado_id}/{foto_id}`.
 *
 * Re-encode SIEMPRE desde canvas: el JPEG persistido nace de píxeles decodificados,
 * sin EXIF ni GPS del original por construcción. Techo duro de bytes (la foto
 * comparte la cuota del origen con LA COLA): se baja calidad y dimensión hasta
 * entrar, y si no entra no se guarda.
 *
 * La subida espera el recibo de la apertura del silo —el ACK: sin silo en el servidor
 * no hay dónde colgar la evidencia (§5.3: 404 hasta entonces)— y recién entonces manda
 * el binario. El borrado del blob local es irreversible: solo ocurre
 * contra un acuse VALIDADO, hash incluido. Es la cola de la v2 (foto del ticket de
 * balanza, `app/datos/fotos.ts` hasta el commit 0ce5e0d) adaptada al PUT
 * versionado por `foto_id` (Agata, 14/9: «la agregamos ahora»).
 */

import { generarUlid } from '../../src/ulid';
import { actualizarPreferencias, alCompletar, promesa, type FallaFoto, type FotoLocal, type MotivoFallaFoto, type Sesion } from './bd';

export const MAX_BYTES_FOTO = 300_000;

export interface IntentoCompresion {
	maxPx: number;
	calidad: number;
}

/** Escalera de compresión, pura y testeable: null = no hay más intentos. */
export function planDeCompresion(intento: number): IntentoCompresion | null {
	const escalera: IntentoCompresion[] = [
		{ maxPx: 1600, calidad: 0.7 },
		{ maxPx: 1600, calidad: 0.5 },
		{ maxPx: 1200, calidad: 0.4 },
		{ maxPx: 900, calidad: 0.3 },
	];
	return escalera[intento] ?? null;
}

export function escalaPara(ancho: number, alto: number, maxPx: number): { ancho: number; alto: number } {
	const mayor = Math.max(ancho, alto);
	if (mayor <= maxPx) return { ancho, alto };
	const factor = maxPx / mayor;
	return { ancho: Math.round(ancho * factor), alto: Math.round(alto * factor) };
}

async function decodificar(archivo: File): Promise<ImageBitmap | HTMLImageElement> {
	if ('createImageBitmap' in globalThis) {
		try {
			return await createImageBitmap(archivo);
		} catch {
			// caer al <img> (formatos/teléfonos viejos)
		}
	}
	const url = URL.createObjectURL(archivo);
	try {
		const imagen = new Image();
		imagen.src = url;
		await imagen.decode();
		return imagen;
	} finally {
		URL.revokeObjectURL(url);
	}
}

function aBlob(canvas: HTMLCanvasElement, calidad: number): Promise<Blob | null> {
	return new Promise((resolver) => canvas.toBlob(resolver, 'image/jpeg', calidad));
}

/**
 * File de la cámara → JPEG re-encodeado bajo el techo de bytes. Tira si el
 * archivo no es una imagen decodificable o el canvas falla (la UI ofrece
 * reintentar o seguir sin foto).
 */
export async function comprimirFoto(archivo: File): Promise<Blob> {
	const imagen = await decodificar(archivo);
	const anchoOriginal = 'width' in imagen ? imagen.width : 0;
	const altoOriginal = 'height' in imagen ? imagen.height : 0;
	try {
		for (let intento = 0; ; intento++) {
			const plan = planDeCompresion(intento);
			if (!plan) break;
			const { ancho, alto } = escalaPara(anchoOriginal, altoOriginal, plan.maxPx);
			const canvas = document.createElement('canvas');
			canvas.width = ancho;
			canvas.height = alto;
			const ctx = canvas.getContext('2d');
			if (!ctx) throw new Error('sin canvas 2d');
			ctx.drawImage(imagen, 0, 0, ancho, alto);
			const blob = await aBlob(canvas, plan.calidad);
			if (!blob) throw new Error('el canvas no pudo generar el JPEG');
			if (blob.size <= MAX_BYTES_FOTO) return blob;
		}
		// Techo DURO: si ni el último escalón entra, no se guarda.
		throw new Error('la foto no entra en el límite de espacio');
	} finally {
		if ('close' in imagen) imagen.close();
	}
}

// ── El blob y el borrador ──────────────────────────────────────────────────

/**
 * Guarda la foto recién sacada junto con el borrador que la declara, en UNA
 * transacción: si el proceso muere en el medio, no puede quedar un borrador que
 * diga «con foto» sin blob, ni un blob que ningún borrador reclame. Solo si el
 * borrador SIGUE en la base —una compresión que termina después de confirmar o
 * descartar no lo resucita— y la foto anterior del borrador (la que dice la fila
 * guardada, no la que vio la pantalla) se borra solo si no está confirmada (Codex,
 * ronda del diff, 14/9). Devuelve false si el borrador ya no estaba.
 */
export async function guardarFotoDelBorrador(bd: IDBDatabase, foto: FotoLocal, borrador: { capture_id: string }): Promise<boolean> {
	const tx = bd.transaction(['fotos', 'borradores'], 'readwrite');
	const borradores = tx.objectStore('borradores');
	const fotos = tx.objectStore('fotos');
	const guardado = (await promesa(borradores.get(borrador.capture_id))) as { foto?: { foto_id: string } | null } | undefined;
	if (!guardado) {
		await alCompletar(tx);
		return false;
	}
	const previaId = guardado.foto?.foto_id ?? null;
	if (previaId !== null && previaId !== foto.foto_id) {
		const previa = (await promesa(fotos.get(previaId))) as FotoLocal | undefined;
		if (previa && !previa.confirmada) fotos.delete(previaId);
	}
	fotos.put(foto);
	borradores.put(borrador);
	await alCompletar(tx);
	return true;
}

export async function leerFoto(bd: IDBDatabase, fotoId: string): Promise<FotoLocal | undefined> {
	return (await promesa(bd.transaction('fotos').objectStore('fotos').get(fotoId))) as FotoLocal | undefined;
}

export async function borrarFotoLocal(bd: IDBDatabase, fotoId: string): Promise<void> {
	const tx = bd.transaction('fotos', 'readwrite');
	tx.objectStore('fotos').delete(fotoId);
	await alCompletar(tx);
}

/**
 * Confirma una foto por fuera de `encolar` (herramientas y tests). La app confirma la
 * foto DENTRO de la transacción que encola la apertura (`encolar(..., fotosAConfirmar)`).
 */
export async function marcarFotoConfirmada(bd: IDBDatabase, fotoId: string): Promise<void> {
	const tx = bd.transaction('fotos', 'readwrite');
	const store = tx.objectStore('fotos');
	const actual = (await promesa(store.get(fotoId))) as FotoLocal | undefined;
	if (actual && !actual.confirmada) store.put({ ...actual, confirmada: true });
	await alCompletar(tx);
}

// ── Cola de subida (§5.3) ──────────────────────────────────────────────────

export const MAX_FOTOS_POR_PASADA = 3;
export const PRESUPUESTO_FOTOS_MS = 20_000;
/** Tres minutos: 300 KB a 32 kbit/s efectivos tardan más de uno, y cada abort empezaba de cero (Codex, 14/9). */
const TIMEOUT_PUT_MS = 180_000;

/** Qué entidad sube cada estación (§5.2.8): el resto ni lo intenta. */
const ENTIDAD_POR_ESTACION: Record<string, FotoLocal['entidad']> = { produccion: 'llenado' };

export interface ResultadoFotos {
	subidas: number;
	falladas: number;
	/** Candidatas que no entraron por el tope o el presupuesto de la pasada, más las re-identificadas. */
	quedan: number;
	/** 410: la reconciliación reclamó la key (§5.3, issue #24); la foto sigue acá con otro `foto_id` y sube en la pasada siguiente. */
	reidentificadas: number;
	/** Red, 5xx o acuse inesperado: conviene reintentar con backoff. */
	transitorio: boolean;
	tokenMuerto: boolean;
}

/** Acuse del servidor, validado ANTES de borrar la única copia de la evidencia. */
export function esAcuseDeFoto(cuerpo: unknown): cuerpo is { estado: 'subida'; sha256: string } {
	if (typeof cuerpo !== 'object' || cuerpo === null) return false;
	const { estado, sha256 } = cuerpo as Record<string, unknown>;
	return estado === 'subida' && typeof sha256 === 'string' && /^[0-9a-f]{64}$/.test(sha256);
}

export async function sha256HexDeBlob(blob: Blob): Promise<string> {
	const hash = await crypto.subtle.digest('SHA-256', await blob.arrayBuffer());
	return [...new Uint8Array(hash)].map((byte) => byte.toString(16).padStart(2, '0')).join('');
}

/**
 * Dónde está la apertura que crea el silo. La foto sube con el ACK en la mano (el recibo
 * de `llenado`), no por ausencia en la cola: una apertura que nunca se encoló, o cuyo
 * encolado falló, sigue esperando en vez de estrellarse contra un 404 (Codex, 14/9). Una
 * apertura caída —en la bandeja, o acusada y recordada en `config.caidas`— es un incidente.
 */
async function estadoDeLaApertura(bd: IDBDatabase, foto: FotoLocal): Promise<'espera' | 'incidente' | 'lista'> {
	const tx = bd.transaction(['outbox', 'recibos', 'config']);
	const fila = (await promesa(tx.objectStore('outbox').get(foto.mutacion_id))) as { estado_local?: string } | undefined;
	if (fila) return fila.estado_local === 'rechazada' || fila.estado_local === 'conflicto' ? 'incidente' : 'espera';
	const recibo = await promesa(tx.objectStore('recibos').get([foto.entidad, foto.entidad_id]));
	if (recibo) return 'lista';
	const caidas = ((await promesa(tx.objectStore('config').get('caidas'))) as string[] | undefined) ?? [];
	return caidas.includes(foto.mutacion_id) ? 'incidente' : 'espera';
}

async function todasLasFotos(bd: IDBDatabase): Promise<FotoLocal[]> {
	// Son pocas (las de los silos abiertos en este teléfono sin señal): materializarlas entra.
	const todas = (await promesa(bd.transaction('fotos').objectStore('fotos').getAll())) as FotoLocal[];
	return todas.sort((a, b) => (a.capturada_en < b.capturada_en ? -1 : a.capturada_en > b.capturada_en ? 1 : a.foto_id < b.foto_id ? -1 : 1));
}

/**
 * La reconciliación reclamó la key de esta foto (410, issue #24): la key está muerta, la evidencia no.
 * Se vuelve a guardar con OTRO `foto_id` —mismo blob, mismo hash, misma apertura— en una transacción,
 * solo si sigue acá, confirmada y sin falla (otra pestaña pudo resolverla). Un `foto_id` confirmado no
 * lo referencia nadie más: el borrador ya no existe y la apertura viaja en `mutacion_id`.
 */
export async function reidentificarFoto(bd: IDBDatabase, fotoId: string): Promise<string | null> {
	const tx = bd.transaction('fotos', 'readwrite');
	const store = tx.objectStore('fotos');
	const actual = (await promesa(store.get(fotoId))) as FotoLocal | undefined;
	let nuevoId: string | null = null;
	if (actual && actual.confirmada && !actual.falla) {
		nuevoId = generarUlid();
		store.delete(fotoId);
		store.put({ ...actual, foto_id: nuevoId });
	}
	await alCompletar(tx);
	return nuevoId;
}

async function marcarFallaFoto(bd: IDBDatabase, fotoId: string, falla: FallaFoto): Promise<void> {
	const tx = bd.transaction('fotos', 'readwrite');
	const store = tx.objectStore('fotos');
	const actual = (await promesa(store.get(fotoId))) as FotoLocal | undefined;
	if (actual && !actual.falla) store.put({ ...actual, falla });
	await alCompletar(tx);
}

/**
 * Las fotos listas para subir: confirmadas, sin falla y con la apertura ya fuera
 * de la cola. Una foto cuya apertura cayó como incidente se marca fallada acá
 * mismo: no tiene dónde colgarse y el encargado tiene que verla.
 */
export async function fotosParaSubir(bd: IDBDatabase): Promise<FotoLocal[]> {
	const listas: FotoLocal[] = [];
	for (const foto of await todasLasFotos(bd)) {
		if (!foto.confirmada || foto.falla) continue;
		const estado = await estadoDeLaApertura(bd, foto);
		if (estado === 'espera') continue;
		if (estado === 'incidente') {
			await marcarFallaFoto(bd, foto.foto_id, { codigo: 0, motivo: 'entidad_rechazada', en: new Date().toISOString() });
			continue;
		}
		listas.push(foto);
	}
	return listas;
}

/**
 * Borra el blob ya subido, SOLO si sigue siendo el binario que se envió
 * (compare-and-delete): entre el PUT y el acuse, otra pestaña pudo reemplazar la
 * fila, y borrar por id destruiría una foto que el servidor nunca recibió. El hash
 * se guardó al capturar, así que se compara DENTRO de la transacción del borrado:
 * no hay ventana entre comparar y borrar (Codex, ronda del diff, 14/9).
 */
async function borrarFotoSubida(bd: IDBDatabase, fotoId: string, shaEnviado: string): Promise<boolean> {
	const tx = bd.transaction('fotos', 'readwrite');
	const store = tx.objectStore('fotos');
	const actual = (await promesa(store.get(fotoId))) as FotoLocal | undefined;
	const esLaEnviada = actual !== undefined && actual.sha256 === shaEnviado;
	if (esLaEnviada) store.delete(fotoId);
	await alCompletar(tx);
	return esLaEnviada;
}

const FALLAS: Record<number, MotivoFallaFoto> = {
	403: 'estacion_no_autorizada',
	404: 'entidad_inexistente',
	409: 'foto_resuelta',
	411: 'sin_largo',
	413: 'demasiado_grande',
};

/**
 * Sube las fotos cuya apertura ya salió de la cola. Regla dura: un fallo
 * transitorio NO escribe nada en IndexedDB (reescribir la fila arrastraría el
 * blob en cada intento); solo los terminales dejan marca.
 */
export async function subirFotosPendientes(
	bd: IDBDatabase,
	sesion: Sesion,
	traer: typeof fetch = fetch,
	opciones: { tope?: number; presupuestoMs?: number } = {},
): Promise<ResultadoFotos> {
	const resultado: ResultadoFotos = { subidas: 0, falladas: 0, quedan: 0, reidentificadas: 0, transitorio: false, tokenMuerto: false };
	// Se listan primero: marcar las aperturas caídas no depende de la estación.
	const candidatas = await fotosParaSubir(bd);
	// Un dispositivo de otra estación se comería un 403 por foto: ni intentar. Las fotos quedan
	// retenidas y la pantalla lo dice.
	const entidad = sesion.estacion === null ? undefined : ENTIDAD_POR_ESTACION[sesion.estacion];
	if (!entidad) return resultado;

	const tope = opciones.tope ?? MAX_FOTOS_POR_PASADA;
	const presupuestoMs = opciones.presupuestoMs ?? PRESUPUESTO_FOTOS_MS;
	const arranque = Date.now();
	let enviadas = 0;

	for (const candidata of candidatas) {
		if (candidata.entidad !== entidad) continue;
		if (enviadas >= tope || Date.now() - arranque > presupuestoMs) {
			resultado.quedan += 1;
			continue;
		}
		const foto = await leerFoto(bd, candidata.foto_id);
		if (!foto || foto.falla) continue; // otra pestaña la resolvió entre la lista y acá
		enviadas += 1;

		let respuesta: Response;
		try {
			respuesta = await traer(`/api/fotos/${foto.entidad}/${foto.entidad_id}/${foto.foto_id}`, {
				method: 'PUT',
				headers: { 'Content-Type': 'image/jpeg', Authorization: `Bearer ${sesion.token}` },
				body: foto.blob,
				signal: AbortSignal.timeout(TIMEOUT_PUT_MS),
			});
		} catch {
			resultado.transitorio = true;
			return resultado;
		}

		if (respuesta.status === 401) {
			resultado.tokenMuerto = true;
			return resultado;
		}
		if (respuesta.status === 410) {
			// La key está muerta, la foto no: otro `foto_id` y a la pasada siguiente. Ni falla ni bandeja.
			if ((await reidentificarFoto(bd, foto.foto_id)) !== null) resultado.reidentificadas += 1;
			resultado.quedan += 1;
			continue;
		}
		const motivo = FALLAS[respuesta.status];
		if (motivo) {
			await marcarFallaFoto(bd, foto.foto_id, { codigo: respuesta.status, motivo, en: new Date().toISOString() });
			resultado.falladas += 1;
			// El 403 es del dispositivo, no de la foto: el resto correría la misma suerte.
			if (respuesta.status === 403) return resultado;
			continue;
		}
		if (!respuesta.ok) {
			resultado.transitorio = true;
			return resultado;
		}

		// 200: se valida el acuse ANTES de borrar. `respuesta.ok` no alcanza —un portal
		// cautivo también devuelve 200— y el hash tiene que ser el de ESTOS bytes: es
		// la prueba de que el servidor guardó exactamente esta foto.
		const cuerpo = await respuesta.json().catch(() => null);
		const shaLocal = foto.sha256;
		if (!esAcuseDeFoto(cuerpo) || cuerpo.sha256 !== shaLocal) {
			await actualizarPreferencias(bd, {
				ultimo_error_fotos: `El servidor respondió algo inesperado al subir una foto (silo ${foto.entidad_id}).`,
			});
			resultado.transitorio = true; // jamás terminal: un bug del server no quema la evidencia
			return resultado;
		}
		await borrarFotoSubida(bd, foto.foto_id, shaLocal);
		resultado.subidas += 1;
	}

	if (resultado.subidas > 0) {
		await actualizarPreferencias(bd, { ultimo_error_fotos: undefined });
	}
	return resultado;
}

// ── Lo que ve el encargado ─────────────────────────────────────────────────

/** El encargado da otra chance a una falla revisable (403/413). */
export async function limpiarFallaFoto(bd: IDBDatabase, fotoId: string): Promise<void> {
	const tx = bd.transaction('fotos', 'readwrite');
	const store = tx.objectStore('fotos');
	const actual = (await promesa(store.get(fotoId))) as FotoLocal | undefined;
	if (actual?.falla) {
		const { falla: _descartada, ...resto } = actual;
		store.put(resto);
	}
	await alCompletar(tx);
}

/** Acuse del encargado: solo borra si SIGUE fallada (nunca una foto sana). */
export async function acusarFotoFallada(bd: IDBDatabase, fotoId: string): Promise<void> {
	const tx = bd.transaction('fotos', 'readwrite');
	const store = tx.objectStore('fotos');
	const actual = (await promesa(store.get(fotoId))) as FotoLocal | undefined;
	if (actual?.falla) store.delete(fotoId);
	await alCompletar(tx);
}

/** Fotos que fallaron de forma terminal, para la pantalla del encargado. */
export async function bandejaDeFotos(bd: IDBDatabase): Promise<FotoLocal[]> {
	return (await todasLasFotos(bd)).filter((foto) => foto.falla);
}

/**
 * Pendientes = confirmadas y sin falla (esperen o no el acuse de la apertura).
 * Falladas = TODAS las marcadas: una foto de un silo rechazado también es
 * evidencia que el encargado tiene que resolver, y no puede quedar invisible.
 */
export async function contarFotos(bd: IDBDatabase): Promise<{ fotos: number; fotosFalladas: number; fotoMasViejaMs: number | null }> {
	let fotos = 0;
	let fotosFalladas = 0;
	let masVieja: number | null = null;
	for (const foto of await todasLasFotos(bd)) {
		if (foto.falla) {
			fotosFalladas += 1;
			continue;
		}
		if (!foto.confirmada) continue; // todavía es un borrador
		fotos += 1;
		const ms = Date.parse(foto.capturada_en);
		if (!Number.isNaN(ms)) masVieja = Math.min(masVieja ?? ms, ms);
	}
	return { fotos, fotosFalladas, fotoMasViejaMs: masVieja };
}
